import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send } from 'lucide-react';
import { LegalLayout } from '../components/LegalLayout';
import { getApiBaseUrl } from '../../../lib/config';
import '../legal.css';

type Topic = 'general' | 'sales' | 'support' | 'security' | 'press';

const TOPIC_OPTIONS: { value: Topic; label: string; mailto: string }[] = [
  { value: 'general', label: 'General question', mailto: 'hello@rynora.app' },
  { value: 'sales', label: 'Sales / Enterprise', mailto: 'support@rynora.app' },
  { value: 'support', label: 'Customer support', mailto: 'support@rynora.app' },
  { value: 'security', label: 'Security disclosure', mailto: 'ops@rynora.app' },
  { value: 'press', label: 'Press / media', mailto: 'hello@rynora.app' },
];

function isTopic(v: string): v is Topic {
  return TOPIC_OPTIONS.some((t) => t.value === v);
}

export function ContactPage() {
  const [searchParams] = useSearchParams();
  const initialTopic: Topic = (() => {
    const raw = searchParams.get('topic') ?? '';
    return isTopic(raw) ? raw : 'general';
  })();

  const [topic, setTopic] = useState<Topic>(initialTopic);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const fallbackMail = useMemo(
    () => TOPIC_OPTIONS.find((o) => o.value === topic)?.mailto || 'hello@rynora.app',
    [topic],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          topic,
          message: message.trim(),
          source: 'website-contact-form',
        }),
      });
      if (res.ok) {
        setSuccess(true);
        setName('');
        setEmail('');
        setMessage('');
      } else {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error || `Could not deliver your message. Please email us directly at ${fallbackMail}.`);
      }
    } catch {
      setError(`Network error. Please email us directly at ${fallbackMail}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout
      title="Contact us"
      updated="May 7, 2026"
      summary="Pick a topic and write to us — we route every message to the right inbox. For urgent security issues, email ops@rynora.app directly."
      needsReview={false}
    >
      {success ? (
        <div className="contact-success">
          Thanks — your message is in. We aim to reply within 48 hours (24 hours for security reports). If you don't
          hear back, email <a href={`mailto:${fallbackMail}`}>{fallbackMail}</a>.
        </div>
      ) : (
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label className="auth-label" htmlFor="contact-topic">
              Topic
            </label>
            <select
              id="contact-topic"
              className="auth-select"
              value={topic}
              onChange={(e) => setTopic(e.target.value as Topic)}
            >
              {TOPIC_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="contact-name">
              Name
            </label>
            <input
              id="contact-name"
              className="auth-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              required
            />
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="contact-email">
              Email
            </label>
            <input
              id="contact-email"
              className="auth-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="contact-message">
              Message
            </label>
            <textarea
              id="contact-message"
              className="auth-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              minLength={10}
              maxLength={4000}
              required
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button className="auth-submit" type="submit" disabled={submitting}>
            {submitting ? (
              <span className="auth-spinner" />
            ) : (
              <>
                <Send size={16} /> Send message
              </>
            )}
          </button>

          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            Or email us directly: <a href={`mailto:${fallbackMail}`}>{fallbackMail}</a>
          </p>
        </form>
      )}
    </LegalLayout>
  );
}
